<template>
    <div class="record-receipt-entries">
        <b-row>
            <csp-desktop-info-card
                :cols="12"
                icon="inticon-autoclaim_billing"
                title="Übersicht abzurechnende Artikel">
                <div class="mb-2">
                    <b-button title="Artikel hinzufügen" variant="secondary"
                        @click="showCreationModal()">
                        Artikel hinzufügen
                    </b-button>
                </div>

                <div class="record-receipt-entries-block">
                    <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
                        <csp-loading-indicator/>
                    </div>

                    <div v-else-if="receiptItems.length === 0" class="collapse-body">
                        <b-alert class="mb-0" show variant="info">
                            Es wurden keine zugeordneten Artikel gefunden.
                        </b-alert>
                    </div>

                    <b-card v-for="receiptItem in receiptItems" :key="receiptItem.id" class="mt-4"
                        :title="receiptItem.attributes.articleName">
                        <b-card-text>
                            <div class="receipt-serviced-info">
                                <div v-if="!receiptItem.attributes.dateCancelled" class="right">
                                    <b-button title="Artikel stornieren" variant="secondary"
                                        @click="showCancellationModal(receiptItem)">
                                        Artikel stornieren
                                    </b-button>
                                </div>
                                <p>Artikelzuordnungs-ID: {{ receiptItem.id }}</p>
                                <p>Leistungsempfänger: {{ receiptItem.attributes.serviceRecipientFirm }}</p>
                                <p>Leistungsdatum: {{ formatDate(receiptItem.attributes.serviceProvidedDate) }}</p>
                                <p>
                                    Aktenfirma während Leistungserbringung: {{ receiptItem.attributes.carDealerFirm }}
                                </p>
                                <p>Belegnummer: {{ receiptItem.attributes.receiptNumber }}</p>
                                <p>Belegpositions-ID: {{ receiptItem.attributes.receiptItemId }}</p>
                                <p>Buchungs-Vermerk: {{ receiptItem.attributes.serviceProvidedNote }}</p>
                                <p v-if="receiptItem.attributes.serviceProvidedInternalNote">
                                    Interner Buchungshinweis: {{ receiptItem.attributes.serviceProvidedInternalNote }}
                                </p>
                            </div>
                            <div v-if="receiptItem.attributes.dateCancelled" class="receipt-cancelled-info mt-1">
                                <p><b>Artikel wurde storniert</b></p>
                                <p>Stornierungsdatum: {{ formatDate(receiptItem.attributes.dateCancelled) }}</p>
                                <p>Stornobelegnummer: {{ receiptItem.attributes.cancellationReceiptNumber }}</p>
                                <p>Belegpositions-ID: {{ receiptItem.attributes.cancellationReceiptItemId }}</p>
                                <p>Artikelzuordnungs-ID: {{ receiptItem.id }}</p>
                                <p>Stornierungs-Vermerk: {{ receiptItem.attributes.cancellationNote }}</p>
                                <p v-if="receiptItem.attributes.cancellationInternalNote">
                                    Interner Stornierungshinweis: {{ receiptItem.attributes.cancellationInternalNote }}
                                </p>
                            </div>
                        </b-card-text>
                    </b-card>

                    <b-modal v-model="isCancellationModalShown" hide-footer size="lg" title="Artikel stornieren"
                        @close="hideCancellationModal()">
                        <div v-if="cancellationModalLoading"
                            class="loading-overlay d-flex align-items-center justify-content-center">
                            <csp-loading-indicator/>
                        </div>
                        <div v-if="entryForCancellation" class="receipt-entry-cancellation-modal">
                            <p>
                                Artikelzuordnungs-ID: {{ entryForCancellation.id }}
                                <br>
                                Leistungsempfänger: {{ entryForCancellation.attributes.serviceRecipientFirm }}
                                <br>
                                Leistungsdatum: {{ formatDate(entryForCancellation.attributes.serviceProvidedDate) }}
                                <br>
                                Belegnummer: {{ entryForCancellation.attributes.receiptNumber }}
                                <br>
                                Buchungs-Vermerk: {{ entryForCancellation.attributes.serviceProvidedNote }}
                                <span v-if="entryForCancellation.attributes.serviceProvidedInternalNote">
                                    Interner Buchungshinweis:
                                    {{ entryForCancellation.attributes.serviceProvidedInternalNote }}
                                </span>
                            </p>
                            <p>
                                <b>Stornierungsinformationen</b>
                            </p>
                            <p>
                                Stornierungsdatum: {{ new Date().toLocaleDateString() }}
                            </p>
                            <b-form class="receipt-entry-form info-card editMode" @submit.prevent="cancelReceiptEntry">
                                <csp-form-group-textarea class="info-card-element"
                                     :form="cancellationForm" property="internalNote"/>
                                <csp-form-group-textarea class="info-card-element"
                                     :form="cancellationForm" property="note"/>
                                <csp-hide-for-group
                                    class="info-card-element"
                                    :form="cancellationForm"
                                    property="hideForGroup"
                                    :record-id="recordId"/>
                                <button class="btn btn-primary btn-action" type="submit">
                                    Artikel stornieren
                                </button>
                            </b-form>
                        </div>
                    </b-modal>

                    <b-modal v-model="isCreationModalShown"
                        hide-footer size="lg" title="Artikel erstellen" @close="hideCreationModal()">
                        <div v-if="creationModalLoading > 0"
                            class="loading-overlay d-flex align-items-center justify-content-center">
                            <csp-loading-indicator/>
                        </div>
                        <div class="receipt-entry-booking-modal">
                            <p>
                                <b>Artikel zu Akte hinzufügen:</b>
                            </p>
                            <p>
                                Leistungsdatum: {{ new Date().toLocaleDateString() }}
                            </p>
                            <b-form class="receipt-entry-form info-card editMode" @submit.prevent="createReceiptEntry">
                                <csp-form-group-select-searchable
                                    class="info-card-element"
                                    :form="creationForm"
                                    :options="serviceRecipientFirmIdOptions"
                                    property="serviceRecipientFirmId"
                                    @change="loadArticleOptions"/>
                                <csp-form-group-select-searchable
                                    class="info-card-element"
                                    :form="creationForm"
                                    :options="invoicingModalityArticleIdOptions"
                                    property="invoiceModalityArticleId"/>
                                <csp-form-group-textarea
                                    class="info-card-element"
                                    :form="creationForm" property="internalNote"/>
                                <csp-form-group-textarea
                                    class="info-card-element"
                                    :form="creationForm" property="note"/>
                                <csp-hide-for-group
                                    class="info-card-element"
                                    :form="creationForm"
                                    property="hideForGroup"
                                    :record-id="recordId"/>
                                <button class="btn btn-primary btn-action" type="submit">
                                    Artikel hinzufügen
                                </button>
                            </b-form>
                        </div>
                    </b-modal>
                </div>
            </csp-desktop-info-card>
        </b-row>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import {formatDate} from '@js/functions/date';
    import Form from '@form/Form';
    import '@desktop/scss/components/record/receipt-entries.scss'

    const container = Container.getInstance();
    const recordRepository = container.getRecordRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        provide() {
            return {
                // define empty function to satisfy injection in InfoCard
                emitFieldChange: () => {},
            }
        },
        data() {
            const recordId = parseInt(this.$route.params.recordId);
            const cancellationFormData = {
                internalNote: '',
                note: '',
                hideForGroup: [],
            };
            const cancellationForm = new Form(cancellationFormData);
            const cancellationFormDataInitial = {...cancellationFormData};
            const creationFormData = {
                invoiceModalityArticleId: '',
                serviceRecipientFirmId: '',
                internalNote: '',
                note: '',
                hideForGroup: [],
            };
            const creationForm = new Form(creationFormData);
            const creationFormDataInitial = {...creationFormData};
            return {
                recordId,
                receiptItems: [],
                isLoading: true,
                isCancellationModalShown: false,
                entryForCancellation: null,
                cancellationForm,
                cancellationFormDataInitial,
                isCreationModalShown: false,
                cancellationModalLoading: false,
                creationForm,
                creationFormDataInitial,
                creationModalLoading: 0,
                serviceRecipientFirmIdOptions: [],
                invoicingModalityArticleIdOptions: [],
            }
        },
        mounted() {
            this.loadEntries();
        },
        methods: {
            cancelReceiptEntry() {
                this.cancellationModalLoading = true;
                this.cancellationForm.clearValidationErrors();
                recordRepository.cancelReceiptEntry(
                    this.recordId,
                    this.entryForCancellation.id,
                    this.cancellationForm
                ).then(
                    () => {
                        flashMessenger.addSuccess('Der Artikel wurde erfolgreich storniert');
                        this.cancellationModalLoading = false;
                        this.hideCancellationModal();
                        this.loadEntries();
                    },
                    (error) => {
                        this.cancellationForm.setValidationErrors(error);
                        this.cancellationModalLoading = false;
                    }
                );
            },
            createReceiptEntry() {
                this.creationModalLoading++;
                this.creationForm.clearValidationErrors();
                recordRepository.createReceiptEntry(
                    this.recordId,
                    this.creationForm
                ).then(
                    () => {
                        flashMessenger.addSuccess('Der Artikel wurde erfolgreich hinzugefügt');
                        this.creationModalLoading = false;
                        this.hideCreationModal();
                        this.loadEntries();
                    },
                    (error) => {
                        this.creationForm.setValidationErrors(error);
                        this.creationModalLoading--;
                    }
                );
            },
            formatDate,
            hideCancellationModal() {
                this.isCancellationModalShown = false;
                this.cancellationModalLoading = false;
                this.entryForCancellation = null;
                this.cancellationForm.setData(this.cancellationFormDataInitial);
            },
            hideCreationModal() {
                this.isCreationModalShown = false;
                this.creationModalLoading = 0;
                this.creationForm.setData(this.creationFormDataInitial);
            },
            loadArticleOptions() {
                const serviceRecipientFirmId = this.creationForm.getDataOfProperty('serviceRecipientFirmId');
                if (!serviceRecipientFirmId) {
                    // no service recipient selected, therefore no modality article options
                    this.invoicingModalityArticleIdOptions = [];
                    return;
                }

                this.creationModalLoading++;
                recordRepository.getInvoiceModalityArticleOptions(this.recordId, serviceRecipientFirmId)
                    .then(
                        (options) => {
                            this.invoicingModalityArticleIdOptions = options;
                            this.creationModalLoading--;
                        }
                    );
            },
            loadEntries() {
                this.isLoading = true;
                recordRepository.findReceiptEntries(this.recordId).then(
                    success => {
                        this.receiptItems = success.data;
                        this.isLoading = false;
                    }
                );
            },
            loadServiceRecipients() {
                this.creationModalLoading++;
                recordRepository.getServiceRecipientOptions(this.recordId).then(
                    (options) => {
                        this.serviceRecipientFirmIdOptions = options;
                        this.creationModalLoading--;
                    }
                );
            },
            showCancellationModal(receiptEntry) {
                this.isCancellationModalShown = true;
                this.entryForCancellation = receiptEntry;
            },
            showCreationModal() {
                this.isCreationModalShown = true;
                this.loadServiceRecipients();
            },
        },
    }
</script>
